/**
 * ScrollLockController - Reactive Controller für Scroll-Locking auf Body-Ebene
 *
 * - Speichert die aktuelle Scroll-Position beim Lock
 * - Sperrt das Scrollen des Body (inkl. optionalem Scrollbar-Width-Workaround)
 * - Stellt beim Unlock die vorherige Scroll-Position wieder her
 *
 * Hinweis:
 * Die Begrenzung der sichtbaren Menü-Höhe passiert in der Host-Komponente
 * (z.B. `bmw-header`) über ein eigenes max-height/overflow-y Handling.
 *
 * @author assistant
 */

import type { ReactiveController, ReactiveControllerHost } from 'lit'

export class ScrollLockController implements ReactiveController {
    private _host: ReactiveControllerHost
    private _isLocked: boolean = false
    private _savedScrollTop: number = 0
    private _originalOverflowY: string = ''
    private _originalPaddingRight: string = ''

    constructor(host: ReactiveControllerHost) {
        this._host = host
        host.addController(this)
    }

    hostConnected(): void {
        // Keine automatische Aktion – Lock/Unlock wird explizit vom Host gesteuert
    }

    hostDisconnected(): void {
        // Sicherheitshalber beim Entfernen entsperren
        if (this._isLocked) {
            this.unlock()
        }
    }

    /**
     * Sperrt das Scrollen des Body und speichert die aktuelle Scroll-Position.
     */
    lock(): void {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return
        }

        if (this._isLocked) {
            return
        }

        this._isLocked = true

        // Aktuelle Scroll-Position merken
        const docEl = document.documentElement
        this._savedScrollTop = window.pageYOffset || docEl.scrollTop || document.body.scrollTop || 0

        const body = document.body

        // Ursprüngliche Inline-Styles merken (wir fassen nur Inline-Styles an)
        this._originalOverflowY = body.style.overflowY
        this._originalPaddingRight = body.style.paddingRight

        // Scrollbar-Width ermitteln, um Layout-Shift zu minimieren
        const scrollbarWidth = window.innerWidth - docEl.clientWidth
        if (scrollbarWidth > 0) {
            body.style.paddingRight = `${scrollbarWidth}px`
        }

        // Body-Scroll sperren
        body.style.overflowY = 'hidden'

        // Bestehendes CSS-Variant-Pattern nutzen (falls verwendet)
        body.classList.add('is-nav-toggled')

        this._host.requestUpdate()
    }

    /**
     * Hebt das Scroll-Locking wieder auf und stellt die Scroll-Position her.
     */
    unlock(): void {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return
        }

        if (!this._isLocked) {
            return
        }

        this._isLocked = false

        const body = document.body

        // Ursprüngliche Inline-Styles wiederherstellen
        body.style.overflowY = this._originalOverflowY
        body.style.paddingRight = this._originalPaddingRight

        body.classList.remove('is-nav-toggled')

        // Ursprüngliche Scroll-Position wiederherstellen
        window.scrollTo({
            top: this._savedScrollTop,
            left: 0,
        })

        this._host.requestUpdate()
    }
}

